import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Selection } from 'monaco-editor';

import { LoggerService } from '../../logger.service';
import { MonacoStandaloneCodeEditor } from '@materia-ui/ngx-monaco-editor';
import { ImageService } from '../image.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import remarkStarter from 'remark-md2html-starter';

@Component({
  selector: 'app-md-editor',
  templateUrl: './md-editor.component.html',
  styleUrls: ['./md-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: MdEditorComponent,
    },
  ],
})
export class MdEditorComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  md = remarkStarter({ allowHtml: true });
  monacoEditor?: MonacoStandaloneCodeEditor;
  theme: string = 'md-theme-default';

  editorOptions = {
    language: 'markdown',
    automaticLayout: true,
  };
  html$ = new BehaviorSubject<string>('');
  extraData$ = new BehaviorSubject<Record<string, any>>({});

  @ViewChild('preview') previewEl?: ElementRef;

  @Input()
  code = '';
  @Output()
  codeChange = new EventEmitter();

  @Output()
  htmlChange = new EventEmitter();
  @Output()
  extraDataChange = new EventEmitter();

  constructor(
    private logger: LoggerService,
    private imageService: ImageService
  ) {
    this.extraData$.subscribe((data) => {
      this.extraDataChange.emit(data);
    });
    this.html$.subscribe((val) => {
      this.htmlChange.emit(val);
    });
  }

  onEditorInit(editor: MonacoStandaloneCodeEditor) {
    this.monacoEditor = editor;
    this.monacoEditor.onDidScrollChange((e) => {
      const el = this.previewEl?.nativeElement;
      if (!el) return;
      const editorScrollableHeight = Math.max(
        e.scrollHeight - this.monacoEditor!.getLayoutInfo().height,
        1
      );
      const previewScrollableHeight = Math.max(
        el.scrollHeight - el.clientHeight,
        0
      );
      el.scrollTop =
        previewScrollableHeight * (e.scrollTop / editorScrollableHeight);
    });
  }

  onCodeChange(code: string) {
    this.code = code;
    this.codeChange.emit(code);
    this.updateHtml();
  }

  async updateHtml(code?: string) {
    const data = await this.md.process(code || this.code);
    this.extraData$.next(data.data);
    this.html$.next(String(data.value));
  }

  insertHeader(level: number) {
    if (!this.monacoEditor) return;
    const range = this.monacoEditor.getSelection()!;
    const model = this.monacoEditor.getModel();
    const lineContent = model?.getLineContent(range.getPosition().lineNumber);
    const text = `${Array(level).fill('#').join('')} `;
    if (lineContent?.startsWith(text)) {
      this.monacoEditor.executeEdits('toolbar', [
        {
          text: '',
          range: {
            ...range,
            startColumn: 1,
            endColumn: text.length + 1,
          },
          forceMoveMarkers: true,
        },
      ]);
    } else {
      this.monacoEditor.executeEdits('toolbar', [
        {
          text,
          range: {
            ...range,
            startColumn: 1,
            endColumn: 1,
          },
          forceMoveMarkers: true,
        },
      ]);
    }
    this.monacoEditor.focus();
  }

  insertBold() {
    if (!this.monacoEditor) return;
    const range = this.monacoEditor.getSelection()!;
    const model = this.monacoEditor.getModel()!;
    const selectContent = model.getValueInRange(range);
    this.monacoEditor.executeEdits(
      'toolbar',
      [
        {
          text: `**${selectContent}**`,
          range,
          forceMoveMarkers: true,
        },
      ],
      (ops) => {
        return ops.map(({ range }) => {
          const endColumn = range.endColumn - 2;
          return new Selection(
            range.startLineNumber,
            endColumn,
            range.endLineNumber,
            endColumn
          );
        });
      }
    );
    this.monacoEditor.focus();
  }

  insertItalic() {
    if (!this.monacoEditor) return;
    const range = this.monacoEditor.getSelection()!;
    const model = this.monacoEditor.getModel()!;
    const selectContent = model.getValueInRange(range);
    this.monacoEditor.executeEdits(
      'toolbar',
      [
        {
          text: `*${selectContent}*`,
          range,
          forceMoveMarkers: true,
        },
      ],
      (ops) => {
        return ops.map(({ range }) => {
          const endColumn = range.endColumn - 2;
          return new Selection(
            range.startLineNumber,
            endColumn,
            range.endLineNumber,
            endColumn
          );
        });
      }
    );
    this.monacoEditor.focus();
  }

  insertInlineCodeBlock() {
    if (!this.monacoEditor) return;
    const range = this.monacoEditor.getSelection()!;
    const model = this.monacoEditor.getModel()!;
    const selectContent = model.getValueInRange(range);
    this.monacoEditor.executeEdits(
      'toolbar',
      [
        {
          text: `\`${selectContent}\``,
          range,
          forceMoveMarkers: true,
        },
      ],
      (ops) => {
        return ops.map(({ range }) => {
          const endColumn = range.endColumn - 1;
          return new Selection(
            range.startLineNumber,
            endColumn,
            range.endLineNumber,
            endColumn
          );
        });
      }
    );
    this.monacoEditor.focus();
  }

  insertCodeBlock() {
    if (!this.monacoEditor) return;
    const range = this.monacoEditor.getSelection()!;
    const model = this.monacoEditor.getModel()!;
    const selectContent = model.getValueInRange(range);
    const lineContent = model.getLineContent(range.startLineNumber);
    const isNewLine = lineContent.length === 0;
    this.monacoEditor.executeEdits(
      'toolbar',
      [
        {
          text: `${isNewLine ? '' : '\n'}\`\`\`
${selectContent}
\`\`\``,
          range,
          forceMoveMarkers: true,
        },
      ],
      (ops) => {
        return ops.map(({ range }) => {
          const column = range.startColumn + 3;
          const lineNumber = range.startLineNumber + (isNewLine ? 0 : 1);
          return new Selection(lineNumber, column, lineNumber, column);
        });
      }
    );
    this.monacoEditor.focus();
  }

  insertImage(
    url: string,
    options: {
      alt?: string;
      name?: string;
      width?: number;
      height?: number;
    } = {}
  ) {
    if (!this.monacoEditor) return;
    const { alt, name, width, height } = options;
    const range = this.monacoEditor.getSelection()!;
    this.monacoEditor.executeEdits('toolbar', [
      {
        text: `![${alt || 'img-alt'}](${url}?width=${width}&height=${height} "${
          name || 'image-name'
        }")`,
        range: {
          ...range,
          startColumn: 0,
          endColumn: 0,
        },
        forceMoveMarkers: true,
      },
    ]);
    this.monacoEditor.focus();
  }

  selectFile() {
    const fileInput = document.createElement('input');
    fileInput.accept = 'image/*';
    fileInput.type = 'file';
    fileInput.addEventListener('change', (evt) => {
      const element = evt.target as HTMLInputElement;
      if (element.files && element.files.length) {
        this.imageService.uploadImage(element.files[0]).then((res) => {
          this.insertImage(res.url);
        });
      }
    });
    fileInput.click();
  }

  handlePaste(event: ClipboardEvent) {
    console.log(event.type, event.clipboardData?.files);
    if (event.type === 'paste' && event.clipboardData?.files?.length) {
      event.preventDefault();
      const file = event.clipboardData.files[0];
      this.imageService.uploadImage(file).then((res) => {
        this.insertImage(res.url);
      });
    }
  }

  ngOnInit(): void {
    this.updateHtml();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.code.currentValue) {
      this.updateHtml(changes.code.currentValue);
    }
  }

  registerOnChange(fn: any): void {
    this.codeChange.subscribe((code) => {
      fn(code);
    });
  }

  registerOnTouched(fn: any): void {}

  setDisabledState(isDisabled: boolean): void {
    this.monacoEditor?.updateOptions({ readOnly: isDisabled });
  }

  writeValue(obj: any): void {
    this.code = obj || '';
    this.updateHtml(this.code);
  }
}
